import { ReactElement, useEffect, useState } from 'react'
import { AsyncThunkAction, AsyncThunkConfig } from '@reduxjs/toolkit'
import { useForm } from '@tanstack/react-form'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'

import axios, { pluckError } from 'src/utilities/http-client'
import { CancelIcon, SubmitIcon } from 'src/v3/core/icons'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'

import {
  fetchExternalCertificates,
  fetchRecertificationCycleCategories,
  selectExternalCertificates,
  selectExternalCertificatesStatus,
  selectRecertificationCategories,
  selectRecertificationCategoriesStatus,
  selectRecertificationCycles,
} from 'src/v3/profile/profile.slice'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { useNotifications } from 'src/v3/hooks/use-notifications'
import { RecertificationCycleExternalCertificate } from 'src/v3/core/types'

dayjs.extend(isBetween)

type ExternalCertificateFormProps = {
  cycleID: string
  externalCertificate?: Partial<RecertificationCycleExternalCertificate>
  refetchCycles: AsyncThunkAction<any, any, AsyncThunkConfig>
  open: boolean
  setOpen: (status: boolean) => void
}

export let ExternalCertificateForm = ({
  cycleID,
  externalCertificate,
  refetchCycles,
  open,
  setOpen,
}: ExternalCertificateFormProps): ReactElement => {
  let dispatch = useAppDispatch()
  let { showError, NotificationComponent } = useNotifications()

  let externalCertificates = useAppSelector(selectExternalCertificates)
  let externalCertificatesStatus = useAppSelector(
    selectExternalCertificatesStatus,
  )
  let allCycles = useAppSelector(selectRecertificationCycles)
  let allCategories = useAppSelector(selectRecertificationCategories)
  let categoriesStatus = useAppSelector(selectRecertificationCategoriesStatus)

  let [saving, setSaving] = useState(false)

  let cycle = allCycles.find((rc) => rc.id === cycleID)
  let categories = allCategories[cycleID || ''] || []
  let allExternalCerts = categories.flatMap((cat) => cat.externalCertificates)
  let allExternalCertsFiltered = allExternalCerts.filter(
    (ec) =>
      ec.externalCertificateID === externalCertificate?.externalCertificateID,
  )

  useAutoFetch(externalCertificatesStatus, fetchExternalCertificates)

  useEffect(() => {
    if (
      cycleID &&
      (!categoriesStatus[cycleID] ||
        categoriesStatus[cycleID] === 'uninitialized')
    ) {
      dispatch(fetchRecertificationCycleCategories(cycleID))
    }
  }, [cycleID, categoriesStatus])

  let form = useForm({
    defaultValues: {
      externalCertificate: externalCertificate?.externalCertificateID || '',
      categories: !allExternalCertsFiltered.length
        ? [
            {
              id: undefined,
              category: externalCertificate?.categoryID || 'unselected',
              hours: '',
            },
          ]
        : allExternalCertsFiltered.map((ec) => ({
            id: ec.id,
            category: ec?.categoryID || 'unselected',
            hours: ec.hours?.toString() || '',
          })),
    },
    onSubmit: async ({ value, formApi }) => {
      setSaving(true)
      let ids = value.categories.map((catJoin) => catJoin.id)
      Promise.all([
        ...allExternalCertsFiltered
          .filter((ec) => !ids.includes(ec.id))
          .map((ec) =>
            axios.delete(
              `/api/profile/recertification-cycle/external-certificate/${ec.id}`,
            ),
          ),
        ...value.categories.map((cat) =>
          !cat.id
            ? axios.post<RecertificationCycleExternalCertificate>(
                `/api/profile/recertification-cycle/external-certificate`,
                {
                  externalCertificateID: value.externalCertificate,
                  categoryID: cat.category,
                  hours: cat.hours,
                },
              )
            : axios.put<RecertificationCycleExternalCertificate>(
                `/api/profile/recertification-cycle/external-certificate`,
                {
                  id: cat.id,
                  externalCertificateID: value.externalCertificate,
                  categoryID: cat.category,
                  hours: cat.hours,
                },
              ),
        ),
      ])
        .then(() => dispatch(fetchRecertificationCycleCategories(cycleID)))
        .then(() => dispatch(refetchCycles))
        .then(() => setOpen(false))
        .then(() => formApi.reset())
        .catch((err) => {
          showError?.(
            pluckError(
              err,
              'Error Creating Recertification Cycle External Certificate',
            ),
          )
        })
        .finally(() => setSaving(false))
    },
  })

  let handleOnClose = () => {
    form.reset()
    setOpen(false)
  }

  return (
    <Dialog open={open} onClose={handleOnClose}>
      <Paper sx={{ width: 600, px: 2, py: 1 }}>
        <DialogTitle sx={{ textAlign: 'center', mb: -3 }}>
          Add External Certificate
        </DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <DialogContent
            sx={{
              px: 2,
              pb: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Typography>1. Select External Certificate</Typography>
            <form.Field
              name="externalCertificate"
              validators={{
                onChange: ({ value }) =>
                  !value || value === 'unselected'
                    ? 'External Certificate is a required field'
                    : undefined,
              }}
              listeners={{
                onChange: ({ value }) => {
                  if (form.state.values.categories.length === 1) {
                    form.setFieldValue(
                      'categories[0].hours',
                      externalCertificates
                        .find((ec) => ec.id === value)
                        ?.length?.toString() || '',
                    )
                  }
                },
              }}
              children={(field) => (
                <Autocomplete
                  value={externalCertificates.find(
                    (ec) => field.state.value === ec.id,
                  )}
                  onChange={(_, value) => {
                    field.handleChange(value?.id || '')
                  }}
                  options={externalCertificates.filter(
                    (ec) =>
                      dayjs(ec.dateCompleted).isBetween(
                        dayjs(cycle?.issueDate),
                        dayjs(cycle?.expirationDate),
                      ) &&
                      (externalCertificate?.externalCertificateID === ec.id ||
                        !allExternalCerts.some(
                          (rcec) => rcec.externalCertificateID === ec.id,
                        )),
                  )}
                  getOptionKey={(option) => option.id}
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, option) => {
                    // eslint-disable-next-line fp/no-rest-parameters
                    let { key, ...optionProps } = props
                    return (
                      <Box
                        key={key}
                        component="li"
                        {...optionProps}
                        sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                      >
                        <Typography sx={{ flex: '1 1 400px' }}>
                          {option.name}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${option.length} Hour${option.length === 1 ? '' : 's'}`}
                        />
                      </Box>
                    )
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={{ bgcolor: 'background.paper' }}
                      label="External Certificate"
                      error={field.state.meta.errors.length > 0}
                      helperText={field.state.meta.errors.join(', ')}
                    />
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                />
              )}
            />
            <form.Subscribe
              selector={(state) => state.values.externalCertificate}
              children={(ec) => {
                let hours =
                  externalCertificates.find((ext) => ext.id === ec)?.length || 0
                return (
                  <form.Field
                    name="categories"
                    mode="array"
                    validators={{
                      onSubmit: ({ value }) =>
                        value.reduce(
                          (sum, c) => sum + parseFloat(c.hours),
                          0,
                        ) !== hours
                          ? `All hours must be assigned & all category hours must add up to ${hours} hours.`
                          : undefined,
                    }}
                  >
                    {(field) => (
                      <>
                        {!ec && (
                          <Typography
                            color="textSecondary"
                            variant="subtitle2"
                            sx={{ pl: 0.5 }}
                          >
                            *Please note that this form allows you to select
                            from pre-uploaded external certificates. If you have
                            not yet uploaded the certificate that you wish to
                            apply to this recertification cycle, you can do so{' '}
                            <Link
                              href="/profile/certificates"
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="none"
                            >
                              here
                            </Link>
                            .
                          </Typography>
                        )}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-end',
                            gap: 0.5,
                          }}
                        >
                          <Typography sx={{ flexGrow: 1 }}>
                            2. Assign {hours} Hour{hours === 1 ? '' : 's'}
                          </Typography>
                          {}
                          <Button
                            color="secondary"
                            sx={{
                              mb: -0.25,
                            }}
                            size="small"
                            onClick={() => {
                              field.pushValue({
                                id: undefined,
                                category: 'unselected',
                                hours: '',
                              })
                            }}
                          >
                            Add Category
                          </Button>
                          <Button
                            color="error"
                            sx={{
                              mb: -0.25,
                            }}
                            size="small"
                            onClick={() =>
                              field.removeValue(field.state.value.length - 1)
                            }
                            disabled={field.state.value.length === 1}
                          >
                            Remove Category
                          </Button>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                          }}
                        >
                          {field.state.value.map((_, i) => (
                            <Box key={i} sx={{ display: 'flex', gap: 1 }}>
                              <form.Field
                                name={`categories[${i}].category`}
                                validators={{
                                  onSubmit: ({ value }) =>
                                    value === 'unselected'
                                      ? 'Category is a required field'
                                      : undefined,
                                }}
                                children={(subField) => (
                                  <FormControl
                                    fullWidth
                                    error={
                                      subField.state.meta.errors.length > 0
                                    }
                                  >
                                    <InputLabel
                                      id={`ext-cert-category-label-${i}`}
                                    >
                                      Category
                                    </InputLabel>
                                    <Select
                                      labelId={`ext-cert-category-label-${i}`}
                                      value={subField.state.value}
                                      label="Category"
                                      onChange={(e) =>
                                        subField.handleChange(e.target.value)
                                      }
                                      sx={{ bgcolor: 'background.paper' }}
                                    >
                                      <MenuItem value="unselected">
                                        Unselected
                                      </MenuItem>
                                      {categories
                                        .filter(
                                          (category) =>
                                            category.id ===
                                              subField.state.value ||
                                            !field.state.value
                                              .map((c) => c.category)
                                              .includes(category.id),
                                        )
                                        .toSorted((a, b) =>
                                          `${a.name}${a.subcategory || ''}`.localeCompare(
                                            `${b.name}${b.subcategory || ''}`,
                                          ),
                                        )
                                        .map((category) => (
                                          <MenuItem
                                            key={category.id}
                                            value={category.id}
                                          >
                                            {category.name}{' '}
                                            {category.subcategory &&
                                              `(${category.subcategory})`}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                    {subField.state.meta.errors.length > 0 && (
                                      <FormHelperText>
                                        {subField.state.meta.errors.join(', ')}
                                      </FormHelperText>
                                    )}
                                  </FormControl>
                                )}
                              />
                              <form.Field
                                name={`categories[${i}].hours`}
                                validators={{
                                  onSubmit: ({ value }) =>
                                    !parseFloat(value)
                                      ? 'Hours is a required field & must be greater than 0'
                                      : undefined,
                                }}
                                children={(subField) => (
                                  <TextField
                                    sx={{ bgcolor: 'background.paper' }}
                                    fullWidth
                                    variant="outlined"
                                    value={subField.state.value}
                                    label="Hours"
                                    name={subField.name}
                                    onChange={(e) => {
                                      subField.handleChange(
                                        e.target.value.replace(/[^\d.]/g, ''),
                                      )
                                      field.validate('change')
                                    }}
                                    onBlur={subField.handleBlur}
                                    error={
                                      subField.state.meta.errors.length > 0
                                    }
                                    helperText={subField.state.meta.errors.join(
                                      ', ',
                                    )}
                                  />
                                )}
                              />
                            </Box>
                          ))}
                          {field.state.meta.errors.length > 0 && (
                            <FormHelperText sx={{ color: 'error.main' }}>
                              {field.state.meta.errors.join(', ')}
                            </FormHelperText>
                          )}
                        </Box>
                      </>
                    )}
                  </form.Field>
                )
              }}
            />
          </DialogContent>
          <DialogActions
            sx={{
              display: 'flex',
              justifyContent: 'center',
              pb: 1.5,
            }}
          >
            <form.Subscribe
              selector={(state) => state.isValid}
              children={(isValid) => (
                <Button
                  color="primary"
                  type="submit"
                  variant="contained"
                  sx={{
                    flex: '1 1 0px',
                  }}
                  startIcon={<SubmitIcon />}
                  disabled={!isValid}
                  loading={saving}
                >
                  Submit
                </Button>
              )}
            />
            <Button
              color="error"
              onClick={handleOnClose}
              variant="contained"
              sx={{
                flex: '1 1 0px',
              }}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
        {NotificationComponent}
      </Paper>
    </Dialog>
  )
}
